import React, { useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { Calendar } from 'primereact/calendar';
import { findNextDay } from '@utils/dateWithTz.tsx';
import { shortDateFormat } from '@utils/dateWithTz.tsx';
import { Button } from 'primereact/button';
import dayjs from 'dayjs';
import { BlueIcon, Clickable } from '@components/styles.tsx';
import { DayEnum } from '@api/ringil-api.v2.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import { useTranslation } from 'react-i18next';
import { useDuplicateDateOptions } from '@components/features/DuplicateDatesButtons/duplicateDateOptions.ts';

interface ComponentProps {
  startLocalDate: string;
  computedLocalDates: string[];
  onChanged: (computedDates: string[]) => void;
  dataTestId?: string;
}

const DuplicateDatesButtons: React.FC<ComponentProps> = ({ startLocalDate, onChanged, computedLocalDates, dataTestId }) => {
  const [customDate, setCustomDate] = useState(false);
  const { tr } = useTranslationLgs();
  const [selectedDate, setSelectedDate] = useState<Date>();

  const addNewPlusDay = (days: number) => {
    const last = computedLocalDates[computedLocalDates.length - 1] || startLocalDate;
    const newLocalDate = dayjs(last).add(days, 'day').format('YYYY-MM-DD');
    onChanged([...computedLocalDates, newLocalDate]);
  };

  const addNewNewDayInWeek = (dayInWeek: DayEnum) => {
    const lastLocalDate = computedLocalDates[computedLocalDates.length - 1];
    const l = dayjs(lastLocalDate);

    const next = findNextDay(l, dayInWeek);
    onChanged([...computedLocalDates, next.format('YYYY-MM-DD')]);
  };

  const { i18n } = useTranslation();
  const { shiftOptions, weekDayOptions } = useDuplicateDateOptions();

  return (
    <>
      {customDate && (
        <div className={'flex flex-wrap items-center gap-2'}>
          <Calendar
            dateFormat={shortDateFormat}
            style={{ width: '140px' }}
            locale={i18n.language}
            showButtonBar={true}
            showIcon
            value={selectedDate}
            onChange={e => setSelectedDate(e.value as Date)}
          />
          <Button
            label={tr(`duplicate.save`, `Save`)}
            onClick={() => {
              onChanged([...computedLocalDates, dayjs(selectedDate).format('YYYY-MM-DD')]);
              setCustomDate(false);
              setSelectedDate(null);
            }}
          />
          <BlueIcon
            className={'pi pi-trash'}
            onClick={() => {
              setCustomDate(false);
            }}
          />
        </div>
      )}
      <>
        {!customDate && (
          <div className={'flex flex-col gap-2 min-w-0'}>
            <div className={'flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0'}>
              {shiftOptions.map(o => (
                <span key={o.testId} className={'inline-flex items-center gap-2 whitespace-nowrap'}>
                  <Clickable data-test-id={`${dataTestId}-${o.testId}`} onClick={() => addNewPlusDay(o.days)}>
                    {o.label}
                  </Clickable>
                  <span className={'text-content-tertiary'}>|</span>
                </span>
              ))}
              <LinkWithIcon
                label={tr(`duplicate.vlastni`, `Vlastni`)}
                onClick={() => setCustomDate(true)}
                icon={<SvgIconPlus />}
                dataTestId={`${dataTestId}-customDate`}
              />
            </div>
            <div className={'flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0'}>
              {weekDayOptions.map((o, index) => (
                <span key={o.testId} className={'inline-flex items-center gap-2 whitespace-nowrap'}>
                  <Clickable data-test-id={`${dataTestId}-${o.testId}`} onClick={() => addNewNewDayInWeek(o.day)}>
                    {o.label}
                  </Clickable>
                  {index < weekDayOptions.length - 1 && <span className={'text-content-tertiary'}>|</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </>
    </>
  );
};

export default DuplicateDatesButtons;
